import { useEffect, useState, type ReactNode } from 'react';

import { Button } from '@/shared/components/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/shadcn/dialog';
import { Separator } from '@/shared/components/shadcn/separator';

import { toScrapingIntentViewModel } from '../../model/scraping-intent-view-model';
import {
  canRestartIntent,
  canStopIntent,
  useScrapingIntentActions,
} from '../../model/use-scraping-intent-actions';
import { type ScrapingIntentSummary } from '../../types';
import { ScrapingStatusBadge } from '../../ui/scraping-status-badge';
import { ScrapingIntentRunList } from './scraping-intent-run-list';

type ScrapingIntentDetailModalProps = {
  intent: ScrapingIntentSummary | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function ScrapingIntentDetailModal({
  intent,
  open,
  onOpenChange,
}: ScrapingIntentDetailModalProps) {
  const [currentIntent, setCurrentIntent] = useState<ScrapingIntentSummary | null>(intent);
  const { deactivateMutation, reactivateMutation } = useScrapingIntentActions();

  useEffect(() => {
    setCurrentIntent(intent);
  }, [intent]);

  const visibleIntent = currentIntent?.intent_id === intent?.intent_id ? currentIntent : intent;

  if (visibleIntent == null) {
    return null;
  }

  const viewModel = toScrapingIntentViewModel(visibleIntent);
  const canDeactivate = canStopIntent(visibleIntent);
  const canReactivate = canRestartIntent(visibleIntent);
  const showScheduleToggle = canDeactivate || canReactivate;
  const isDeactivating =
    deactivateMutation.isPending && deactivateMutation.variables === visibleIntent.intent_id;
  const isReactivating =
    reactivateMutation.isPending && reactivateMutation.variables === visibleIntent.intent_id;

  const handleToggleSchedule = () => {
    if (canDeactivate) {
      deactivateMutation.mutate(visibleIntent.intent_id, {
        onSuccess: setCurrentIntent,
      });
      return;
    }

    if (canReactivate) {
      reactivateMutation.mutate(visibleIntent.intent_id, {
        onSuccess: setCurrentIntent,
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-240">
        <div className="flex flex-col gap-3 pr-6 sm:flex-row sm:items-start sm:justify-between">
          <DialogHeader className="min-w-0 flex-1">
            <DialogTitle>{viewModel.name}</DialogTitle>
            <DialogDescription>
              Scraping intent {visibleIntent.intent_id} for {viewModel.platform}.
            </DialogDescription>
          </DialogHeader>
          {showScheduleToggle && (
            <Button
              className="self-start sm:shrink-0"
              variant="outline"
              size="sm"
              loading={isDeactivating || isReactivating}
              onClick={handleToggleSchedule}
            >
              {canDeactivate ? 'Deactivate' : 'Reactivate'}
            </Button>
          )}
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <DetailItem label="Status">
            <ScrapingStatusBadge label={viewModel.status} tone={viewModel.statusTone} />
          </DetailItem>
          <DetailItem label="Mode">{viewModel.mode}</DetailItem>
          <DetailItem label="Item per day">{viewModel.itemPerDay}</DetailItem>
          <DetailItem label="Target">{viewModel.target}</DetailItem>
          <DetailItem label="Target detail">{viewModel.targetDescription || '-'}</DetailItem>
          <DetailItem label="Date range">{viewModel.dateRange}</DetailItem>
          <DetailItem label="Created">{viewModel.createdAt}</DetailItem>
          <DetailItem label="Stopped">{viewModel.stoppedAt}</DetailItem>
        </div>

        <Separator />

        <section className="flex flex-col gap-3">
          <div>
            <h3 className="text-sm font-semibold">Run history</h3>
            <p className="text-muted-foreground text-sm">
              Runs are listed separately from scheduler intent state.
            </p>
          </div>
          <ScrapingIntentRunList intentId={visibleIntent.intent_id} />
        </section>
      </DialogContent>
    </Dialog>
  );
}

function DetailItem({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-1 rounded-lg border p-3">
      <dt className="text-muted-foreground text-xs font-medium">{label}</dt>
      <dd className="min-w-0 truncate text-sm">{children}</dd>
    </div>
  );
}
